import type { MouseEvent, ReactNode } from 'react'

import { Icon } from 'components/atomic/Icon'
import { getCoverPhoto } from 'services/cover'
import type { Tournament } from 'types/app'

export type TournamentListCardStatus = 'live' | 'upcoming' | 'completed'

/** The subset of a tournament the list card actually renders. */
export type TournamentListCardData = Pick<
  Tournament,
  'tournament_name' | 'prize_pool' | 'number_of_teams' | 'bracket' | 'game' | 'winner' | 'communityId'
> & {
  cover_photo?: { url?: string } | null
  teams?: unknown[]
  date: { start: string }
}

export interface TournamentListCardProps {
  tournament: TournamentListCardData
  status: TournamentListCardStatus
  isGridView?: boolean
  onClick?: () => void
  onCommunityClick?: (communityId: string) => void
  /** Optional corner action (e.g. admin delete), revealed on hover. */
  cornerAction?: ReactNode
}

/**
 * The tournament card shown on the /tournaments list. Presentational only, so the
 * Create tournament preview can render exactly what organizers will see.
 */
export const TournamentListCard = ({
  tournament,
  status,
  isGridView = false,
  onClick,
  onCommunityClick,
  cornerAction,
}: TournamentListCardProps) => {
  const isLive = status === 'live'

  return (
    <div
      onClick={onClick}
      className={`
        group relative overflow-hidden rounded-lg cursor-pointer
        bg-[#0a0a12] border transition-all duration-300
        ${isGridView ? 'w-full' : 'flex-shrink-0 w-[calc(100vw-48px)] min-w-[260px] max-w-[320px] sm:w-[320px] snap-start'}
        ${isLive
          ? 'border-[#00ff88]/50 shadow-[0_0_30px_rgba(0,255,136,0.15)] hover:shadow-[0_0_40px_rgba(0,255,136,0.25)] hover:border-[#00ff88]'
          : 'border-[#1a1a2e] hover:border-[#00ff88]/30 hover:shadow-[0_0_20px_rgba(0,255,136,0.1)]'
        }
      `}
    >
      {/* Scan line effect for live */}
      {isLive && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden z-10">
          <div className="absolute inset-0 bg-[linear-gradient(transparent_50%,rgba(0,255,136,0.03)_50%)] bg-[length:100%_4px] animate-pulse" />
        </div>
      )}

      {/* Cover */}
      <div className="relative h-36 overflow-hidden">
        <img
          src={tournament?.cover_photo?.url ?? getCoverPhoto(tournament?.tournament_name)}
          alt={tournament.tournament_name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a12] via-[#0a0a12]/60 to-transparent" />

        {/* Status indicator */}
        {isLive && (
          <div className="absolute top-3 right-3 flex items-center gap-2 px-2 py-1 bg-black/80 backdrop-blur-sm rounded border border-[#00ff88]/50">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00ff88] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00ff88]"></span>
            </span>
            <span className="text-[10px] font-mono text-[#00ff88] uppercase tracking-wider">Live</span>
          </div>
        )}

        {/* Game badge */}
        {tournament.game && (
          <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2 py-1 bg-black/70 backdrop-blur-sm rounded">
            {tournament.game.avatar?.url && (
              <img src={tournament.game.avatar.url} alt={tournament.game.name} className="w-4 h-4 rounded" />
            )}
            <span className="text-[10px] text-white/70 font-medium">{tournament.game.name}</span>
          </div>
        )}

        {/* Prize pool overlay */}
        <div className="absolute bottom-3 left-3">
          <div className="text-[10px] text-white/50 font-mono uppercase tracking-wider">Prize</div>
          <div className={`text-lg font-bold ${isLive ? 'text-[#00ff88]' : 'text-white'}`}>
            {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(tournament.prize_pool)}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        <h3 className="font-bold text-white text-sm leading-tight line-clamp-1 group-hover:text-[#00ff88] transition-colors">
          {tournament.tournament_name}
        </h3>

        {/* Community badge */}
        {tournament.communityId && typeof tournament.communityId === 'object' && 'community_name' in tournament.communityId && (
          <span
            onClick={(e: MouseEvent) => {
              e.stopPropagation()
              onCommunityClick?.((tournament.communityId as { _id: string })._id)
            }}
            className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded text-[10px] hover:bg-purple-500/30"
          >
            <Icon name="team" size={10} />
            {(tournament.communityId as { community_name: string }).community_name}
          </span>
        )}

        {/* Stats row */}
        <div className="flex items-center justify-between text-[11px] font-mono">
          <div className="flex items-center gap-3">
            <span className="text-white/50">
              <span className="text-white">{tournament.teams?.length || 0}</span>/{tournament.number_of_teams} teams
            </span>
            <span className="text-white/50">
              {new Date(tournament.date.start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          </div>
          <span className="text-white/30 uppercase text-[10px]">
            {tournament.bracket?.replace('_', ' ') || 'SE'}
          </span>
        </div>

        {/* Winner for completed */}
        {status === 'completed' && tournament.winner?.team_name && (
          <div className="flex items-center gap-2 pt-2 border-t border-white/5 text-[11px]">
            <span className="text-yellow-500">🏆</span>
            <span className="text-white/50">Winner:</span>
            <span className="text-white font-medium">{tournament.winner.team_name}</span>
          </div>
        )}
      </div>

      {cornerAction}
    </div>
  )
}
